import { useEffect, useMemo, useState } from 'react';
import { OverlayDialog } from '../../components/ux/OverlayDialog';
import { SectionTabs } from '../../components/ux/SectionTabs';
import { assignmentTypeLabel } from '../../features/history/sessionHistory';
import {
  loadTutorHistory,
  tutorHistoryAnswerResult,
  type TutorHistoryBundle,
  type TutorHistoryInk,
} from '../../features/history/tutorHistorySupabase';
import { InkReplayViewer } from '../../features/ink/InkReplayViewer';
import {
  loadTutorLearnerInsight,
  loadTutorRoster,
  type TutorLearnerScopeOption,
  type TutorRosterStudent,
} from '../../features/insights/learnerInsightSupabase';
import type { LearnerInsight, LearnerSkillInsight } from '../../features/insights/learnerInsightTypes';
import { createTutorNote, type TutorNoteVisibility } from '../../features/notes/tutorNotesSupabase';
import { subscribeSupabaseLearnerProgressWithStatus, type TutorRealtimeStatus } from '../../features/realtime/tutorRealtime';
import { downloadSupabaseInkDocument } from '../../features/runtime/supabaseAdapter';
import { formatDuration } from '../../features/pilotMath/format';
import type { InkDocumentV1 } from '../../types/ink';

type TutorTab = 'summary' | 'skills' | 'history' | 'notes';

const tabs = [
  { value: 'summary', label: 'Ringkasan', description: 'Posisi belajar sekarang' },
  { value: 'skills', label: 'Kemampuan', description: 'Detail per kemampuan' },
  { value: 'history', label: 'Riwayat', description: 'Sesi dan proses belajar' },
  { value: 'notes', label: 'Catatan', description: 'Observasi tutor' },
];

function scopeKey(scope: Pick<TutorLearnerScopeOption, 'studentId' | 'subjectId'>) {
  return `${scope.studentId}:${scope.subjectId}`;
}

function responseValue(value: unknown) {
  if (!value || typeof value !== 'object') return '—';
  const row = value as Record<string, unknown>;
  return row.value === undefined ? '—' : String(row.value);
}

function formatLastActivity(value: string | null) {
  if (!value) return 'Belum ada aktivitas';
  return new Date(value).toLocaleString('id-ID', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function TutorSkillGroup(props: {
  title: string;
  description: string;
  skills: LearnerSkillInsight[];
  emptyLabel: string;
}) {
  return (
    <section className="card tutor-skill-group">
      <div className="section-heading">
        <div>
          <h2>{props.title}</h2>
          <p className="muted">{props.description}</p>
        </div>
        <span className="availability-pill availability-pill--active">{props.skills.length}</span>
      </div>
      <div className="tutor-skill-list">
        {props.skills.map((skill) => (
          <article className="tutor-skill-row" key={skill.skillId}>
            <div className="tutor-skill-row__copy">
              <strong>{skill.learnerStatement}</strong>
              <span className="muted">Aktivitas terakhir: {formatLastActivity(skill.lastActivityAt)}</span>
            </div>
            <div className="tutor-skill-row__diagnostic" aria-label={`Kemampuan ${skill.masteryPercent}% dengan ${skill.evidenceCount} bukti`}>
              <strong>{skill.masteryPercent}%</strong>
              <span>{skill.evidenceCount} bukti</span>
            </div>
          </article>
        ))}
        {!props.skills.length ? <p className="muted">{props.emptyLabel}</p> : null}
      </div>
    </section>
  );
}

export function CloudTeacherStudentMonitorPage() {
  const [scopes, setScopes] = useState<TutorLearnerScopeOption[]>([]);
  const [roster, setRoster] = useState<TutorRosterStudent[]>([]);
  const [viewMode, setViewMode] = useState<'roster' | 'detail'>('roster');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [rosterQuery, setRosterQuery] = useState('');
  const [rosterSubjectFilter, setRosterSubjectFilter] = useState('all');
  const [rosterAttentionFilter, setRosterAttentionFilter] = useState<'all' | 'attention'>('all');
  const [insight, setInsight] = useState<LearnerInsight | null>(null);
  const [activeTab, setActiveTab] = useState<TutorTab>('summary');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [realtimeCount, setRealtimeCount] = useState(0);
  const [realtimeStatus, setRealtimeStatus] = useState<TutorRealtimeStatus>('connecting');
  const [noteBody, setNoteBody] = useState('');
  const [noteVisibility, setNoteVisibility] = useState<TutorNoteVisibility>('staff_only');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [noteSuccess, setNoteSuccess] = useState('');
  const [history, setHistory] = useState<TutorHistoryBundle | null>(null);
  const [selectedHistorySessionId, setSelectedHistorySessionId] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [replayTitle, setReplayTitle] = useState('');
  const [replayDocument, setReplayDocument] = useState<InkDocumentV1 | null>(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayError, setReplayError] = useState('');

  const selectedScope = useMemo(
    () => scopes.find((scope) => scope.studentId === selectedStudentId && scope.subjectId === selectedSubjectId) ?? null,
    [scopes, selectedStudentId, selectedSubjectId],
  );

  const selectedStudentScopes = useMemo(
    () => scopes.filter((scope) => scope.studentId === selectedStudentId),
    [scopes, selectedStudentId],
  );

  const rosterSubjects = useMemo(() => {
    const map = new Map<string, string>();
    for (const scope of scopes) map.set(scope.subjectId, scope.subjectName);
    return [...map.entries()]
      .map(([subjectId, subjectName]) => ({ subjectId, subjectName }))
      .sort((a, b) => a.subjectName.localeCompare(b.subjectName, 'id'));
  }, [scopes]);

  const filteredRoster = useMemo(() => {
    const query = rosterQuery.trim().toLocaleLowerCase('id-ID');
    return roster.filter((student) => {
      const matchesQuery = !query
        || student.studentName.toLocaleLowerCase('id-ID').includes(query)
        || student.studentCode.toLocaleLowerCase('id-ID').includes(query);
      const matchesSubject = rosterSubjectFilter === 'all'
        || student.subjects.some((subject) => subject.subjectId === rosterSubjectFilter);
      const matchesAttention = rosterAttentionFilter === 'all' || student.attentionCount > 0;
      return matchesQuery && matchesSubject && matchesAttention;
    });
  }, [roster, rosterAttentionFilter, rosterQuery, rosterSubjectFilter]);

  const selectedHistorySession = useMemo(
    () => history?.sessions.find((session) => session.sessionId === selectedHistorySessionId)
      ?? history?.sessions[0]
      ?? null,
    [history, selectedHistorySessionId],
  );

  async function loadSelectedInsight(scope: TutorLearnerScopeOption, quiet = false) {
    if (!quiet) setLoading(true);
    try {
      const next = await loadTutorLearnerInsight(scope.studentId, scope.subjectId);
      setInsight(next);
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Data murid belum dapat dimuat.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void loadTutorRoster()
      .then((bundle) => {
        if (cancelled) return;
        setScopes(bundle.scopes);
        setRoster(bundle.students);
        setLoading(false);
      })
      .catch((cause) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Daftar murid belum dapat dimuat.');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (viewMode !== 'detail' || !selectedScope) return;
    setInsight(null);
    void loadSelectedInsight(selectedScope);
  }, [viewMode, selectedScope?.studentId, selectedScope?.subjectId]);

  useEffect(() => {
    if (viewMode !== 'detail' || !selectedScope) {
      setRealtimeStatus('disconnected');
      return undefined;
    }
    return subscribeSupabaseLearnerProgressWithStatus(
      selectedScope.studentId,
      () => {
        setRealtimeCount((current) => current + 1);
        void loadSelectedInsight(selectedScope, true);
      },
      setRealtimeStatus,
    );
  }, [viewMode, selectedScope?.studentId, selectedScope?.subjectId]);


  useEffect(() => {
    setHistory(null);
    setSelectedHistorySessionId('');
    setHistoryError('');
    setReplayTitle('');
    setReplayDocument(null);
    setReplayError('');
    setNoteBody('');
    setNoteVisibility('staff_only');
    setNoteError('');
    setNoteSuccess('');
  }, [selectedScope?.studentId, selectedScope?.subjectId]);

  useEffect(() => {
    if (viewMode !== 'detail' || activeTab !== 'history' || !selectedScope) return undefined;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError('');
    void loadTutorHistory(selectedScope.studentId, selectedScope.subjectId)
      .then((bundle) => {
        if (cancelled) return;
        setHistory(bundle);
        setSelectedHistorySessionId((current) => current || bundle.sessions[0]?.sessionId || '');
      })
      .catch((cause) => {
        if (!cancelled) setHistoryError(cause instanceof Error ? cause.message : 'Riwayat murid belum dapat dimuat.');
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [viewMode, activeTab, selectedScope?.studentId, selectedScope?.subjectId]);

  function openStudent(studentId: string) {
    const candidates = scopes.filter((scope) => scope.studentId === studentId);
    const scope = (rosterSubjectFilter !== 'all'
      ? candidates.find((candidate) => candidate.subjectId === rosterSubjectFilter)
      : null) ?? candidates[0] ?? null;
    if (!scope) return;
    setSelectedStudentId(scope.studentId);
    setSelectedSubjectId(scope.subjectId);
    setActiveTab('summary');
    setViewMode('detail');
  }

  function changeSelectedStudent(studentId: string) {
    const firstScope = scopes.find((scope) => scope.studentId === studentId);
    setSelectedStudentId(studentId);
    setSelectedSubjectId(firstScope?.subjectId ?? '');
    setActiveTab('summary');
  }

  function returnToRoster() {
    setViewMode('roster');
    setError('');
  }

  async function saveTutorNote() {
    if (!selectedScope) return;
    setNoteSaving(true);
    setNoteError('');
    setNoteSuccess('');
    try {
      await createTutorNote({
        studentId: selectedScope.studentId,
        subjectId: selectedScope.subjectId,
        body: noteBody,
        visibility: noteVisibility,
      });
      setNoteBody('');
      setNoteSuccess(noteVisibility === 'guardian_shared'
        ? 'Catatan disimpan dan dibagikan ke Orang Tua.'
        : 'Catatan internal Tutor disimpan.');
      await loadSelectedInsight(selectedScope, true);
    } catch (cause) {
      setNoteError(cause instanceof Error ? cause.message : 'Catatan belum dapat disimpan.');
    } finally {
      setNoteSaving(false);
    }
  }

  async function openReplay(ink: TutorHistoryInk, title: string) {
    if (!ink.captureReplay || !ink.storageKey) return;
    setReplayTitle(title);
    setReplayDocument(null);
    setReplayError('');
    setReplayLoading(true);
    try {
      const document = await downloadSupabaseInkDocument(ink.storageKey);
      setReplayDocument(document);
    } catch {
      setReplayError('Tulisan belum dapat dimuat. Coba lagi.');
    } finally {
      setReplayLoading(false);
    }
  }

  function closeReplay() {
    setReplayTitle('');
    setReplayDocument(null);
    setReplayError('');
    setReplayLoading(false);
  }

  if (error && !insight && viewMode === 'roster') return <div className="feedback feedback--error">{error}</div>;

  if (!loading && !scopes.length) {
    return (
      <div className="stack">
        <div><h1>Murid</h1><p className="muted">Workspace Tutor BelajarKu.</p></div>
        <section className="card"><h2>Belum ada murid</h2><p className="muted">Belum ada murid dan mata pelajaran aktif yang ditugaskan kepada Tutor ini.</p></section>
      </div>
    );
  }

  return (
    <div className="stack tutor-workspace">
      <div className="tutor-workspace__heading">
        <div>
          <span className="page-kicker">PT-REF-01 · TUTOR</span>
          <h1>{viewMode === 'roster' ? 'Murid' : insight?.learner.displayName ?? selectedScope?.studentName ?? 'Murid'}</h1>
          <p className="muted">
            {viewMode === 'roster'
              ? 'Cari murid, filter mata pelajaran, lalu buka perkembangan tanpa menelusuri dropdown kombinasi yang panjang.'
              : 'Workspace perkembangan murid berdasarkan data belajar authoritative yang sama dengan Student History.'}
          </p>
        </div>
        {viewMode === 'detail' ? (
          <span className={`status-dot status-dot--${realtimeStatus === 'connected' ? 'completed' : 'in_progress'}`}>
            {realtimeStatus === 'connected'
              ? `Realtime tersambung · ${realtimeCount} pembaruan`
              : realtimeStatus === 'reconnecting'
                ? 'Menghubungkan ulang · data terakhir tetap ditampilkan'
                : realtimeStatus === 'disconnected'
                  ? 'Realtime terputus · data terakhir tetap ditampilkan'
                  : 'Menghubungkan realtime…'}
          </span>
        ) : (
          <span className="availability-pill availability-pill--active">{filteredRoster.length} murid</span>
        )}
      </div>

      {viewMode === 'roster' ? (
        <div className="stack tutor-roster">
          <section className="card tutor-roster-toolbar">
            <label className="tutor-roster-toolbar__search" htmlFor="tutor-roster-search">
              <span className="muted">Cari murid</span>
              <input
                id="tutor-roster-search"
                type="search"
                value={rosterQuery}
                onChange={(event) => setRosterQuery(event.target.value)}
                placeholder="Cari nama atau kode murid"
              />
            </label>
            <label htmlFor="tutor-roster-subject-filter">
              <span className="muted">Mata pelajaran</span>
              <select
                id="tutor-roster-subject-filter"
                value={rosterSubjectFilter}
                onChange={(event) => setRosterSubjectFilter(event.target.value)}
              >
                <option value="all">Semua mata pelajaran</option>
                {rosterSubjects.map((subject) => (
                  <option key={subject.subjectId} value={subject.subjectId}>{subject.subjectName}</option>
                ))}
              </select>
            </label>
            <label htmlFor="tutor-roster-attention-filter">
              <span className="muted">Status</span>
              <select
                id="tutor-roster-attention-filter"
                value={rosterAttentionFilter}
                onChange={(event) => setRosterAttentionFilter(event.target.value as 'all' | 'attention')}
              >
                <option value="all">Semua status</option>
                <option value="attention">Perlu perhatian</option>
              </select>
            </label>
          </section>

          <section className="tutor-roster-list" aria-label="Daftar murid Tutor">
            {filteredRoster.map((student) => (
              <article className="card tutor-roster-card" key={student.studentId}>
                <div className="tutor-roster-card__identity">
                  <div>
                    <span className="page-kicker">{student.studentCode}</span>
                    <h2>{student.studentName}</h2>
                  </div>
                  <span className={`availability-pill ${student.attentionCount > 0 ? '' : 'availability-pill--active'}`}>
                    {student.attentionCount > 0 ? `${student.attentionCount} perlu perhatian` : 'Tidak ada perhatian khusus'}
                  </span>
                </div>
                <div className="tutor-roster-card__subjects">
                  {student.subjects.map((subject) => (
                    <span className="tutor-roster-subject-chip" key={subject.subjectId}>
                      {subject.subjectName}
                      {subject.attentionCount > 0 ? ` · ${subject.attentionCount} perhatian` : ''}
                    </span>
                  ))}
                </div>
                <div className="tutor-roster-card__footer">
                  <span className="muted">Aktivitas terakhir: {formatLastActivity(student.lastActivityAt)}</span>
                  <button type="button" onClick={() => openStudent(student.studentId)}>Buka perkembangan</button>
                </div>
              </article>
            ))}
            {!filteredRoster.length && !loading ? (
              <section className="card empty-state">Tidak ada murid yang cocok dengan filter ini.</section>
            ) : null}
            {loading && !roster.length ? <section className="card">Menyiapkan daftar murid…</section> : null}
          </section>
        </div>
      ) : (
        <section className="card tutor-detail-navigation">
          <button className="text-button tutor-detail-navigation__back" type="button" onClick={returnToRoster}>← Semua murid</button>
          <div className="tutor-detail-selectors">
            <label htmlFor="tutor-student-selector">
              <span className="muted">Murid</span>
              <select
                id="tutor-student-selector"
                value={selectedStudentId}
                onChange={(event) => changeSelectedStudent(event.target.value)}
              >
                {roster.map((student) => (
                  <option key={student.studentId} value={student.studentId}>{student.studentName}</option>
                ))}
              </select>
            </label>
            <label htmlFor="tutor-subject-selector">
              <span className="muted">Mata pelajaran</span>
              <select
                id="tutor-subject-selector"
                value={selectedSubjectId}
                onChange={(event) => setSelectedSubjectId(event.target.value)}
              >
                {selectedStudentScopes.map((scope) => (
                  <option key={scopeKey(scope)} value={scope.subjectId}>{scope.subjectName}</option>
                ))}
              </select>
            </label>
          </div>
          {selectedScope ? <p className="muted tutor-detail-navigation__meta">{selectedScope.studentCode} · {selectedScope.subjectName}</p> : null}
        </section>
      )}

      {viewMode === 'detail' ? (
        <>
      <SectionTabs
        value={activeTab}
        tabs={tabs}
        ariaLabel="Bagian workspace Tutor"
        onChange={(value) => setActiveTab(value as TutorTab)}
      />

      {loading && !insight ? <section className="card">Menyiapkan perkembangan murid…</section> : null}
      {error && insight ? <div className="feedback feedback--error">{error}</div> : null}

      {insight && activeTab === 'summary' ? (
        <div className="stack">
          <section className="card student-monitor-card">
            <div className="student-monitor-card__header">
              <div>
                <span className="page-kicker">RINGKASAN MURID</span>
                <h2>{insight.learner.displayName}</h2>
                <p className="muted">{insight.subject.name}</p>
              </div>
              <span className="availability-pill availability-pill--active">{insight.currentLevel?.displayName ?? 'Level belum ditetapkan'}</span>
            </div>
            <div className="grid grid--3 tutor-summary-grid">
              <div className="metric"><span>Sedang dipelajari</span><strong>{insight.skillGroups.learning.length}</strong></div>
              <div className="metric"><span>Perlu perhatian</span><strong>{insight.skillGroups.attention.length}</strong></div>
              <div className="metric"><span>Aktivitas terakhir</span><strong className="tutor-summary-grid__date">{formatLastActivity(insight.lastActivityAt)}</strong></div>
            </div>
          </section>

          <section className="card">
            <div className="section-heading"><div><span className="page-kicker">TARGET BELAJAR</span><h2>Yang sedang dikerjakan</h2></div></div>
            {insight.skillGroups.learning[0] ? (
              <p className="tutor-focus-statement">{insight.skillGroups.learning[0].learnerStatement}</p>
            ) : insight.skillGroups.attention[0] ? (
              <p className="tutor-focus-statement">Penguatan: {insight.skillGroups.attention[0].learnerStatement}</p>
            ) : (
              <p className="muted">Belum ada target aktif pada mata pelajaran ini.</p>
            )}
          </section>
        </div>
      ) : null}

      {insight && activeTab === 'skills' ? (
        <div className="stack">
          <TutorSkillGroup
            title="Perlu perhatian"
            description="Kemampuan yang authoritative state-nya meminta review atau penguatan."
            skills={insight.skillGroups.attention}
            emptyLabel="Tidak ada kemampuan yang sedang membutuhkan perhatian khusus."
          />
          <TutorSkillGroup
            title="Sedang dipelajari"
            description="Kemampuan yang masih berada pada fase belajar atau latihan."
            skills={insight.skillGroups.learning}
            emptyLabel="Belum ada kemampuan aktif pada fase belajar."
          />
          <TutorSkillGroup
            title="Sudah kuat"
            description="Kemampuan yang sudah mencapai state penguasaan authoritative."
            skills={insight.skillGroups.strong}
            emptyLabel="Belum ada kemampuan yang masuk kelompok ini."
          />
        </div>
      ) : null}

      {insight && activeTab === 'history' ? (
        <section className="card bkux-history-panel session-history-panel">
          <div className="section-heading">
            <div>
              <span className="page-kicker">RIWAYAT SESI</span>
              <h2>Proses belajar {insight.learner.displayName}</h2>
            </div>
            <span className="muted section-note">Jawaban pertama · perbaikan · tulisan</span>
          </div>

          {historyLoading && !history ? <div className="empty-state">Menyiapkan riwayat sesi…</div> : null}
          {historyError ? <div className="feedback feedback--error">{historyError}</div> : null}

          {history ? (
            <div className="session-history-layout">
              <div className="session-history-list">
                {history.sessions.map((session) => {
                  const selected = selectedHistorySession?.sessionId === session.sessionId;
                  const firstPassPercent = session.answerCount
                    ? Math.round((session.firstPassCorrect / session.answerCount) * 100)
                    : 0;
                  return (
                    <button
                      className={`session-history-row ${selected ? 'session-history-row--selected' : ''}`}
                      key={session.sessionId}
                      type="button"
                      onClick={() => setSelectedHistorySessionId(session.sessionId)}
                    >
                      <span className="session-history-row__date">
                        {new Date(`${session.learningDate}T00:00:00`).toLocaleDateString('id-ID', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                      <span className="session-history-row__title">
                        {assignmentTypeLabel(session.assignmentType)} · {insight.subject.name}
                      </span>
                      <span className="session-history-row__meta">
                        {session.answerCount} soal · {firstPassPercent}% benar pertama · {formatDuration(session.activeDurationMs)}
                      </span>
                    </button>
                  );
                })}
                {!history.sessions.length ? <div className="empty-state">Belum ada sesi latihan yang tersimpan.</div> : null}
              </div>

              <div className="session-history-detail">
                {selectedHistorySession ? (
                  <>
                    <div className="session-history-detail__hero">
                      <div>
                        <span className="page-kicker">DETAIL SESI</span>
                        <h3>
                          {assignmentTypeLabel(selectedHistorySession.assignmentType)} ·{' '}
                          {new Date(`${selectedHistorySession.learningDate}T00:00:00`).toLocaleDateString('id-ID', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </h3>
                      </div>
                      <span className="availability-pill availability-pill--active">
                        {selectedHistorySession.status === 'completed' ? 'Selesai' : 'Tersimpan'}
                      </span>
                    </div>

                    <div className="session-history-stats">
                      <div><strong>{selectedHistorySession.answerCount}</strong><span>soal</span></div>
                      <div><strong>{selectedHistorySession.firstPassCorrect}</strong><span>benar pertama</span></div>
                      <div><strong>{selectedHistorySession.correctionNeeded}</strong><span>diperbaiki</span></div>
                      <div><strong>{formatDuration(selectedHistorySession.activeDurationMs)}</strong><span>waktu aktif</span></div>
                    </div>

                    <div className="session-history-evidence">
                      {selectedHistorySession.answers.map((answer, index) => (
                        <article className="session-history-answer" key={answer.answerId}>
                          <div>
                            <strong>Soal {index + 1} · {answer.learnerStatement}</strong>
                            <span>
                              {tutorHistoryAnswerResult(answer)}
                              {answer.hintCount > 0 ? ` · ${answer.hintCount} bantuan` : ''}
                              {answer.activeDurationMs > 0 ? ` · ${formatDuration(answer.activeDurationMs)}` : ''}
                            </span>
                          </div>
                          <div className="session-history-answer__actions">
                            {answer.correction ? (
                              <span>
                                Perbaikan {responseValue(answer.correction.previousResponse)} →{' '}
                                {responseValue(answer.correction.correctedResponse)}
                              </span>
                            ) : null}
                            {answer.ink?.captureReplay ? (
                              <button
                                className="text-button"
                                type="button"
                                onClick={() => void openReplay(answer.ink!, `Soal ${index + 1} · ${answer.learnerStatement}`)}
                              >
                                Putar tulisan
                              </button>
                            ) : (
                              <span>Tidak ada rekaman tulisan untuk jawaban ini</span>
                            )}
                          </div>
                        </article>
                      ))}
                      {!selectedHistorySession.answers.length ? (
                        <p className="muted">
                          Detail jawaban sesi ini belum tersedia. Ringkasan sesi tetap ditampilkan tanpa mengarang evidence.
                        </p>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <div className="empty-state">Pilih satu sesi untuk melihat detail proses belajar.</div>
                )}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {insight && activeTab === 'notes' ? (
        <div className="stack">
          <section className="card tutor-note-composer">
            <div className="section-heading">
              <div>
                <span className="page-kicker">CATATAN TUTOR</span>
                <h2>Tambahkan observasi belajar</h2>
              </div>
              <span className="muted section-note">{insight.subject.name}</span>
            </div>

            <label htmlFor="tutor-note-visibility"><strong>Siapa yang dapat melihat?</strong></label>
            <select
              id="tutor-note-visibility"
              value={noteVisibility}
              onChange={(event) => setNoteVisibility(event.target.value as TutorNoteVisibility)}
            >
              <option value="staff_only">Internal Tutor</option>
              <option value="guardian_shared">Bagikan ke Orang Tua</option>
            </select>

            <label htmlFor="tutor-note-body"><strong>Catatan</strong></label>
            <textarea
              id="tutor-note-body"
              rows={5}
              maxLength={2000}
              value={noteBody}
              onChange={(event) => setNoteBody(event.target.value)}
              placeholder="Tuliskan observasi singkat yang membantu tindak lanjut belajar."
            />
            <div className="section-heading">
              <span className="muted">{noteBody.length}/2000</span>
              <button type="button" onClick={() => void saveTutorNote()} disabled={noteSaving || !noteBody.trim()}>
                {noteSaving ? 'Menyimpan…' : 'Simpan catatan'}
              </button>
            </div>
            {noteError ? <div className="feedback feedback--error">{noteError}</div> : null}
            {noteSuccess ? <div className="feedback feedback--success">{noteSuccess}</div> : null}
          </section>

          <section className="card">
            <div className="section-heading">
              <div>
                <span className="page-kicker">RIWAYAT CATATAN</span>
                <h2>{insight.authorizedNotes.length} catatan yang dapat dilihat</h2>
              </div>
            </div>
            <div className="tutor-skill-list">
              {insight.authorizedNotes.map((note) => (
                <article className="tutor-skill-row" key={note.id}>
                  <div className="tutor-skill-row__copy">
                    <strong>{note.body}</strong>
                    <span className="muted">
                      {note.visibility === 'guardian_shared' ? 'Dibagikan ke Orang Tua' : 'Internal Tutor'}
                      {' · '}
                      {formatLastActivity(note.createdAt)}
                    </span>
                  </div>
                </article>
              ))}
              {!insight.authorizedNotes.length ? <p className="muted">Belum ada catatan Tutor untuk mata pelajaran ini.</p> : null}
            </div>
          </section>
        </div>
      ) : null}

        </>
      ) : null}

      {replayTitle || replayLoading || replayDocument || replayError ? (
        <OverlayDialog title={replayTitle || 'Rekaman tulisan'} kicker="REKAMAN TULISAN" onClose={closeReplay}>
          {replayLoading ? <div className="empty-state">Mengambil rekaman tulisan…</div> : null}
          {replayError ? <div className="feedback feedback--error">{replayError}</div> : null}
          {replayDocument ? <InkReplayViewer document={replayDocument} /> : null}
        </OverlayDialog>
      ) : null}
    </div>
  );
}
